import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { GoldDiamond } from "@/components/icons";
import { track } from "@/lib/analytics";

const POINTS = [
  "Rental income earned in India by an NRI can have Indian tax implications.",
  "Rental payments made to an NRI landlord can involve tax withholding (TDS) requirements for the tenant or payer.",
  "The applicable rules depend on the owner's residential status and individual circumstances.",
  "Banking and FEMA rules may apply when funds are remitted outside India.",
  "NRO and NRE account structures — and repatriation requirements — can differ significantly.",
  "Owners should consult their CA, tax professional or bank for advice specific to their situation.",
];

export default function TaxGuide() {
  return (
    <>
      <SEO
        title="NRI Rental Income, TDS & Remittance — What Owners Should Know"
        description="A high-level orientation for NRI property owners on Indian rental income, TDS, FEMA and repatriation — and how NRI Property Services coordinates your rental records."
        path="/nri-tax-tds-guide"
      />
      <PageHero
        eyebrow="NRI Owner Information"
        titleLines={["NRI Rental Income, TDS & Remittance —", "What Owners Should Know"]}
        copy="A plain-language orientation — not tax advice — to help you ask your CA the right questions."
        ctaLabel="Talk to Us About Your Rental Records"
      />

      <section className="bg-ivory py-24 sm:py-32">
        <div className="container-site grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
          <div>
            <Reveal>
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
                The essentials, in plain language
              </h2>
            </Reveal>
            <Stagger className="mt-10 space-y-4" gap={0.07}>
              {POINTS.map((p) => (
                <StaggerItem key={p}>
                  <div className="flex items-start gap-4 rounded-2xl border border-navy-900/10 bg-white p-5">
                    <GoldDiamond className="mt-1.5 h-2 w-2 shrink-0 text-gold-500" />
                    <p className="text-sm leading-relaxed text-navy-900/85 sm:text-base">{p}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>

            <Reveal delay={0.15}>
              <div className="mt-10 flex gap-4 rounded-2xl border border-gold-500/40 bg-gold-300/15 p-6 sm:p-8" data-testid="tax-disclaimer">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-gold-600" />
                <p className="text-xs leading-relaxed text-navy-900/75 sm:text-sm">
                  NRI Property Services is not a tax advisor, law firm or financial consultant. Nothing on this
                  page constitutes tax, legal or financial advice. Applicable tax rates, TDS provisions, FEMA
                  rules and repatriation limits change and depend on your circumstances — please consult your
                  Chartered Accountant, tax professional or bank before making decisions.
                </p>
              </div>
            </Reveal>
          </div>

          <div>
            <Reveal delay={0.1}>
              <div className="rounded-3xl bg-navy-900 p-8 sm:p-10 lg:sticky lg:top-32">
                <h3 className="font-display text-2xl font-extrabold tracking-tight text-white">
                  Where we fit in
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-white/65 sm:text-base">
                  While we don't provide tax advice, we make your CA's job easier: clean monthly rent records,
                  payment receipts, expense documentation and property records — organised and retrievable. Where
                  required, we coordinate directly with your appointed CA, bank or professional advisor.
                </p>
                <Link
                  to="/contact"
                  className="btn-gold mt-8 w-full"
                  data-testid="tax-guide-cta"
                  onClick={() => track("assessment_request", { location: "tax_guide" })}
                >
                  Need help coordinating your rental records? Talk to us <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
